/**
 * IdentifyPanel — optional card identification + ROI metadata.
 *
 * Card identification:
 *   - Type a name (or paste a number/set), POSTed to /api/grading/identify
 *     which proxies the existing pokemontcg integration. Picking a match
 *     autofills cardName + tcgCardId on the parent's `meta` state and the
 *     market price (if returned) auto-fills the rawValue field for ROI.
 *   - Optional Tesseract.js OCR: dynamically imported only when the user
 *     supplies a front image and clicks "Read from photo". The OCR'd name
 *     populates the search field; the user always confirms before submit.
 *
 * Phase 2/3 contract: `onChange(meta)` mutates parent state — IdentifyPanel
 * does no fetching after submit; everything is server-side.
 */
import React, { useState, useRef, useEffect } from 'react';

export default function IdentifyPanel({ meta, onChange, frontImage }) {
  const [open, setOpen] = useState(false);
  const [matches, setMatches] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searchErr, setSearchErr] = useState(null);
  const [picked, setPicked] = useState(null);
  const [name, setName] = useState(meta?.cardName || '');
  const [number, setNumber] = useState('');
  const [set, setSet] = useState('');
  const debounceRef = useRef(null);

  // Debounced auto-search on (name | number | set) change.
  useEffect(() => {
    if (!open) return;
    if (!name && !number) { setMatches([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      runSearch(name, number, set);
    }, 350);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, number, set, open]);

  async function runSearch(name, number, set) {
    setSearching(true); setSearchErr(null);
    try {
      const r = await fetch('/api/grading/identify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, number, set }),
      });
      const j = await r.json();
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setMatches(Array.isArray(j.matches) ? j.matches : []);
    } catch (e) {
      setSearchErr(e.message || 'Search failed');
      setMatches([]);
    } finally {
      setSearching(false);
    }
  }

  function pick(card) {
    setPicked(card);
    // Auto-fill the raw market value from tcgplayer prices when present.
    // Pick the most-relevant variant (holofoil > reverseHolofoil > normal),
    // and use the market price (median sale) when available.
    let rawValue = meta?.rawValue || '';
    const prices = card.tcgPrices || {};
    const pickPrice = (k) => prices?.[k]?.market ?? prices?.[k]?.mid ?? null;
    const v = pickPrice('holofoil') ?? pickPrice('reverseHolofoil') ?? pickPrice('normal') ?? pickPrice('1stEditionHolofoil');
    if (v != null) rawValue = String(v);
    onChange({ ...meta, cardName: card.name, tcgCardId: card.id, rawValue });
    setName(card.name);
  }

  // ---- OCR (lazy Tesseract.js import) ----
  const [ocrBusy, setOcrBusy] = useState(false);
  const [ocrErr, setOcrErr] = useState(null);
  async function runOcr() {
    if (!frontImage) { setOcrErr('Capture or upload the front first.'); return; }
    setOcrBusy(true); setOcrErr(null);
    try {
      // Dynamic import keeps Tesseract.js (~10MB) out of the main bundle.
      const Tess = await import('tesseract.js');
      // Crop the top ~25% of the front image — the title region — to make
      // OCR fast + accurate. Done via a canvas pipe.
      const url = URL.createObjectURL(frontImage);
      const img = await new Promise((res, rej) => {
        const im = new Image(); im.onload = () => res(im); im.onerror = rej; im.src = url;
      });
      const canvas = document.createElement('canvas');
      canvas.width  = img.naturalWidth;
      canvas.height = Math.floor(img.naturalHeight * 0.22);
      canvas.getContext('2d').drawImage(img, 0, 0, img.naturalWidth, canvas.height, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      const { data } = await Tess.recognize(canvas, 'eng');
      // Take the first non-empty line, trim, strip non-letters at edges.
      const line = (data?.text || '').split('\n').map(s => s.trim()).find(Boolean) || '';
      const cleaned = line.replace(/[^A-Za-z0-9' .-]/g, '').trim();
      if (!cleaned) {
        setOcrErr('Could not read a card name from the image. Try better lighting or type it.');
      } else {
        setName(cleaned);
      }
    } catch (e) {
      setOcrErr('OCR failed: ' + (e.message || 'unknown error'));
    } finally {
      setOcrBusy(false);
    }
  }

  function updateMeta(patch) {
    onChange({ ...meta, ...patch });
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto 16px', padding: 12, border: '1px solid #e5e7eb', borderRadius: 8 }}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, color: '#2563eb', padding: 0 }}
      >
        {open ? '▾' : '▸'} Identify card &amp; "Worth grading?" calculator (optional)
      </button>

      {open && (
        <div style={{ marginTop: 12 }}>
          <p style={{ fontSize: 13, color: '#555', marginTop: 0 }}>
            Provide the card name (and optionally number / set) to autofill metadata
            and unlock a market-aware ROI estimate.
          </p>
          <div style={{ marginBottom: 8 }}>
            <button
              type="button" onClick={runOcr} disabled={!frontImage || ocrBusy}
              style={{
                padding: '10px 14px', minHeight: 44, fontSize: 14, borderRadius: 4,
                border: '1px solid #ccc', background: '#fff', cursor: frontImage ? 'pointer' : 'not-allowed',
                opacity: frontImage ? 1 : 0.5,
              }}
              title={frontImage ? 'Run OCR on the captured front image' : 'Capture a front image first'}
            >
              {ocrBusy ? 'Reading photo…' : '📷 Read name from photo'}
            </button>
            {ocrErr && <div role="alert" style={{ marginTop: 6, fontSize: 12, color: '#b91c1c' }}>{ocrErr}</div>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
            <input
              type="text" placeholder="Card name (e.g. Charizard VMAX)"
              value={name} onChange={e => setName(e.target.value)}
              style={input}
            />
            <input
              type="text" placeholder="Number (e.g. 020/189)"
              value={number} onChange={e => setNumber(e.target.value)}
              style={input}
            />
            <input
              type="text" placeholder="Set (e.g. Darkness Ablaze)"
              value={set} onChange={e => setSet(e.target.value)}
              style={input}
            />
          </div>

          {searching && <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Searching…</div>}
          {searchErr && <div role="alert" style={{ fontSize: 13, color: '#b91c1c', marginTop: 6 }}>{searchErr}</div>}

          {matches.length > 0 && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: 8, marginTop: 10 }}>
              {matches.map(c => (
                <button
                  key={c.id}
                  type="button"
                  onClick={() => pick(c)}
                  style={{
                    cursor: 'pointer', border: '1px solid ' + (picked?.id === c.id ? '#2563eb' : '#ddd'),
                    background: picked?.id === c.id ? '#eff6ff' : '#fff', borderRadius: 6, padding: 4,
                    textAlign: 'left',
                  }}
                >
                  {c.image && <img src={c.image} alt={c.name} style={{ width: '100%', borderRadius: 4 }} />}
                  <div style={{ fontSize: 12, fontWeight: 600, marginTop: 4 }}>{c.name}</div>
                  <div style={{ fontSize: 11, color: '#666' }}>{c.set} #{c.number}</div>
                  {c.tcgPrices?.holofoil?.market != null && (
                    <div style={{ fontSize: 11, color: '#16a34a' }}>
                      ${c.tcgPrices.holofoil.market.toFixed(2)} (holo)
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {/* Raw value + currency for ROI */}
          <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ fontSize: 13 }}>
              Raw market value:&nbsp;
              <input type="number" min="0" step="0.01" placeholder="e.g. 25"
                value={meta?.rawValue || ''}
                onChange={e => updateMeta({ rawValue: e.target.value })}
                style={{ ...input, width: 120, display: 'inline-block' }}
              />
            </label>
            <select
              value={meta?.currency || 'USD'}
              onChange={e => updateMeta({ currency: e.target.value })}
              style={{ ...input, width: 'auto' }}
            >
              <option>USD</option><option>CAD</option><option>EUR</option><option>GBP</option>
            </select>
            {picked && (
              <span style={{ fontSize: 12, color: '#16a34a', marginLeft: 'auto' }}>
                ✓ Identified as <strong>{picked.name}</strong>
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const input = {
  padding: '10px 12px', minHeight: 44, border: '1px solid #ccc', borderRadius: 4, fontSize: 16,
};
